import { cx } from '@linaria/core';
import { useQueryClient } from '@tanstack/react-query';
import { memo, useRef } from 'react';
import { useUserIdentityState } from '@provider/user-identity';
import { breakpoints, resets } from '@ui/css';
import { Row, Cell, IconButton } from '@ui/kit';
import { formatTimestamp } from '@utils/common';
import { useMatchBreakpoint, useToggleState } from '@utils/react';
import { deepCompare } from '../../../utils/deep-compare.js';
import { getDialoguesQuery } from '../../dialogues/api.get-dialogues.query.js';
import { DialogOverlay } from '../../dialogues/overlay.dialog.js';
import { EntriesDialogIcon } from '../atoms/icon.dialog.js';
import { Stats } from '../atoms/stats.js';
import { tableModifiers } from '../shared.table.style.js';
import { nestedRowSizingCls, nestedItem, mobileHeadingCls, mobileStatsCell, mobileDialogCell, statsCls } from './table.nested-item.style.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { useDeleteNotificationMutation } from '../../notifications/api.delete-notification.mutation.js';

const NestedItem = /*#__PURE__*/memo(function NestedItem(props) {
  const queryClient = useQueryClient();
  const deleteNotification = useDeleteNotificationMutation();
  const user = useUserIdentityState(state => state.user);
  const isMobile = useMatchBreakpoint(breakpoints.mobile, {
    immediate: true
  });
  const dialogButtonRef = useRef(null);
  const [showDialog, actions] = useToggleState();
  const onDialogueClick = async evt => {
    evt.stopPropagation();
    if (!props.item.dialogue.id) {
      return;
    }
    await queryClient.prefetchQuery(getDialoguesQuery(user, props.item.dialogue.id));
    deleteNotification.mutate(props.item.dialogue.notifications);
    actions.enable();
  };
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx(Row, {
      className: cx(nestedItem, props.item.highlighted && tableModifiers.new.className),
      sizingCls: nestedRowSizingCls,
      onClick: () => props.onClick(props.index),
      children: isMobile ? /*#__PURE__*/jsx(MobileRow, {
        ...props,
        dialogButtonRef: dialogButtonRef,
        onDialogueClick: onDialogueClick
      }) : /*#__PURE__*/jsx(DesktopRow, {
        ...props,
        dialogButtonRef: dialogButtonRef,
        onDialogueClick: onDialogueClick
      })
    }), /*#__PURE__*/jsx(DialogOverlay, {
      active: showDialog,
      pointAt: dialogButtonRef,
      dialogId: props.item.dialogue.id,
      componentTitle: props.item.component.title,
      onClose: actions.disable
    })]
  });
}, deepCompare);
function DesktopRow(props) {
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx(Cell, {
      children: props.item.student.name
    }), /*#__PURE__*/jsx(Cell, {
      children: props.item.student.team
    }), /*#__PURE__*/jsx(Cell, {
      children: formatTimestamp(props.item.modifiedAt ?? props.item.createdAt)
    }), props.withStats && /*#__PURE__*/jsx(Cell, {
      className: statsCls,
      children: /*#__PURE__*/jsx(Stats, {
        score: props.item.status.score
      })
    }), /*#__PURE__*/jsx(Cell, {
      tooltip: false,
      children: /*#__PURE__*/jsx(IconButton, {
        ref: props.dialogButtonRef,
        icon: /*#__PURE__*/jsx(EntriesDialogIcon, {
          height: "19",
          hasNewNotifications: props.item.dialogue.notifications.length > 0
        }),
        label: "Åbn dialog",
        async: {
          showThrobberWhilePending: true
        },
        onClick: props.onDialogueClick
      })
    })]
  });
}
function MobileRow(props) {
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsxs("div", {
      children: [/*#__PURE__*/jsxs("div", {
        children: [/*#__PURE__*/jsxs(Cell, {
          role: "unset",
          "aria-hidden": true,
          className: mobileHeadingCls,
          children: [props.item.student.name, ", ", props.item.student.name, ",", " ", formatTimestamp(props.item.modifiedAt ?? props.item.createdAt)]
        }), /*#__PURE__*/jsx(Cell, {
          className: resets.screenReaderOnlyCls,
          children: props.item.student.name
        }), /*#__PURE__*/jsx(Cell, {
          className: resets.screenReaderOnlyCls,
          children: props.item.student.team
        }), /*#__PURE__*/jsx(Cell, {
          className: resets.screenReaderOnlyCls,
          children: formatTimestamp(props.item.modifiedAt ?? props.item.createdAt)
        })]
      }), props.item.status.score && /*#__PURE__*/jsx(Cell, {
        className: mobileStatsCell,
        children: /*#__PURE__*/jsx(Stats, {
          score: props.item.status.score
        })
      })]
    }), /*#__PURE__*/jsx(Cell, {
      tooltip: false,
      className: mobileDialogCell,
      children: /*#__PURE__*/jsx(IconButton, {
        ref: props.dialogButtonRef,
        icon: /*#__PURE__*/jsx(EntriesDialogIcon, {
          hasNewNotifications: props.item.dialogue.notifications.length > 0,
          height: "16"
        }),
        label: "Åbn dialog",
        async: {
          showThrobberWhilePending: true
        },
        onClick: props.onDialogueClick
      })
    })]
  });
}

export { NestedItem };
//# sourceMappingURL=table.nested-item.js.map
